import React, { useState, useEffect, useMemo, ChangeEvent, useRef } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import {
  CheckCircle,
  X,
  MapPin,
  RefreshCw,
  Wifi,
  WifiOff,
  Map,
  BookOpen,
  Info,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Autocomplete from '@/components/ui/Autocomplete';
import Modal from '@/components/ui/Modal';
import MapDisplay from '@/components/maps/MapDisplay';
import {
  mapCacheService,
  type AddressData,
} from '@/services/maps/MapCacheService';
import { formatPhoneBR } from '@/utils/format';
import { contactsService } from '@/services/contacts/ContactsService';
import { teamsService } from '@/services/teams/TeamsService';
import { useAuthStore } from '@/stores';
import StudyRegistrationModal from '@/components/study/StudyRegistrationModal';
import type { StudyFormData } from '@/components/study/StudyRegistrationModal';
import { offlineStorageService } from '@/services/offline/OfflineStorageService';
import { studySchedulesService, type StudyScheduleStatus } from '@/services/studySchedules/StudySchedulesService';
import { listCities } from '@/services/addresses/AddressCitiesService';
import { listNeighborhoods } from '@/services/addresses/AddressNeighborhoodsService';
import { toast } from 'react-toastify';

interface FormData {
  name: string;
  phone: string;
  email: string;
  locationName: string;
  street: string;
  number: string;
  address_complement: string;
  neighborhood: string;
  city: string;
  state: string;
  gender: 'masculino' | 'feminino';
  age_group: 'crianca' | 'jovem' | 'adulto' | 'senior';
  consent_whatsapp: boolean;
  observations: string;
  studyInterest: boolean;
}

interface FieldCadastroModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: {
    id?: string;
    visitType?: string;
    location?: [number, number];
    name?: string;
    phone?: string;
    email?: string;
    address?: string;
    observations?: string;
  }
}

const FieldCadastroModal: React.FC<FieldCadastroModalProps> = ({
  isOpen,
  onClose,
  initialData,
}) => {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { user } = useAuth();
  const [visitType, setVisitType] = useState(initialData?.visitType || '');
  const [showForm, setShowForm] = useState<boolean>(Boolean(initialData?.visitType));
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [location, setLocation] = useState<[number, number] | null>(
    initialData?.location || null
  );

  // Estado para controlar os modais adicionais
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showStudyModal, setShowStudyModal] = useState(false);
  const [savedContactId, setSavedContactId] = useState<string>('');
  const [offlineCount, setOfflineCount] = useState<number>(0);
  const [savedOnline, setSavedOnline] = useState<boolean>(false);
  const [savedTeamName, setSavedTeamName] = useState<string>('');
  const [activeTeamName, setActiveTeamName] = useState<string>('');

  // Neighborhood options driven by resolved city id
  const [resolvedCityId, setResolvedCityId] = useState<number | null>(null);
  const [neighborhoodOptions, setNeighborhoodOptions] = useState<{ value: string; label: string }[]>([]);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);
  const [showNeighborhoodTip, setShowNeighborhoodTip] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  // City options driven by UF
  const [cityOptions, setCityOptions] = useState<{ value: string; label: string }[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  // UF options (fixed)
  const UF_OPTIONS: { value: string; label: string }[] = [
    'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
  ].map(uf => ({ value: uf, label: uf }));

  // Normalize Brazilian state to UF code (accepts full names like "Paraná")
  function normalizeUF(input?: string | null): string {
    if (!input) return '';
    const v = String(input).trim();
    if (v.length === 2) return v.toUpperCase();
    const map: Record<string, string> = {
      'acre': 'AC', 'alagoas': 'AL', 'amapa': 'AP', 'amapá': 'AP', 'amazonas': 'AM', 'bahia': 'BA', 'ceara': 'CE', 'ceará': 'CE',
      'distrito federal': 'DF', 'espirito santo': 'ES', 'espírito santo': 'ES', 'goias': 'GO', 'goiás': 'GO', 'maranhao': 'MA', 'maranhão': 'MA',
      'mato grosso': 'MT', 'mato grosso do sul': 'MS', 'minas gerais': 'MG', 'para': 'PA', 'pará': 'PA', 'paraiba': 'PB', 'paraíba': 'PB',
      'parana': 'PR', 'paraná': 'PR', 'pernambuco': 'PE', 'piaui': 'PI', 'piauí': 'PI', 'rio de janeiro': 'RJ', 'rio grande do norte': 'RN',
      'rio grande do sul': 'RS', 'rondonia': 'RO', 'rondônia': 'RO', 'roraima': 'RR', 'santa catarina': 'SC', 'sao paulo': 'SP', 'são paulo': 'SP',
      'sergipe': 'SE', 'tocantins': 'TO'
    };
    const key = v.toLowerCase();
    return map[key] ?? '';
  }

  // Basic BR phone validation (10+ digits)
  const isValidPhone = (value: string) => {
    const digits = (value || '').replace(/\D/g, '');
    return digits.length >= 10;
  };

  const [formData, setFormData] = useState<FormData>({
    name: initialData?.name || '',
    phone: initialData?.phone || '',
    email: initialData?.email || '',
    locationName: '',
    street: '',
    number: '',
    address_complement: '',
    neighborhood: '',
    city: '',
    state: '',
    gender: 'masculino',
    age_group: 'adulto',
    consent_whatsapp: false,
    observations: initialData?.observations || '',
    studyInterest: false,
  });

  // Verificar status de conexão
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Helper accessible from any handler: resolve active team and region
  async function resolveTeamAndRegionForSchedules(): Promise<{ teamId: number; regionId: number } | null> {
    const auth = useAuthStore.getState();
    const cachedRegionId = auth.activeRegionId ?? auth.user?.region_id ?? null;
    const cachedTeamId = auth.activeTeamId ?? null;
    if (cachedRegionId && cachedTeamId) {
      return { teamId: cachedTeamId, regionId: cachedRegionId };
    }
    let teamId: number | null = cachedTeamId;
    let regionId: number | null = cachedRegionId;
    try {
      if (!teamId && regionId) {
        const resp = await teamsService.list({ region_id: regionId, per_page: 100 });
        if (resp.data && resp.data.length > 0) {
          teamId = resp.data[0].id;
          regionId = resp.data[0].region_id;
        }
      }
      if (!teamId) {
        const anyTeams = await teamsService.list({ per_page: 100 });
        if (anyTeams.data && anyTeams.data.length > 0) {
          teamId = anyTeams.data[0].id;
          regionId = anyTeams.data[0].region_id;
        }
      }
    } catch (e) {
      console.warn('Falha ao obter times para study_schedules:', e);
    }
    return teamId && regionId ? { teamId, regionId } : null;
  }

  // Initialize offline saved counter when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset flow when opening if no initial visit type
      if (!initialData?.visitType) {
        setVisitType('');
        setShowForm(false);
      }
      try {
        const visits = offlineStorageService.getOfflineVisits();
        setOfflineCount(visits.length);
      } catch (_) {
        setOfflineCount(0);
      }
    }
  }, [isOpen]);

  // Resolve and display user's active team name when modal opens
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const auth = useAuthStore.getState();
        let teamName = '';
        const prefTeamId = auth.user?.active_team_id ?? auth.activeTeamId ?? null;
        if (prefTeamId) {
          try {
            const t = await teamsService.getById(prefTeamId);
            if (t?.name) teamName = t.name;
          } catch {
            /* ignore */
          }
        }
        if (!teamName) {
          try {
            const myTeams = await teamsService.my();
            const active = prefTeamId ? myTeams.find((t) => t.id === prefTeamId) : myTeams[0];
            if (active?.name) teamName = active.name;
          } catch {
            /* ignore */
          }
        }
        setActiveTeamName(teamName);
      } catch {
        setActiveTeamName('');
      }
    })();
  }, [isOpen]);

  const chooseVisitType = (type: 'person_met' | 'not_home' | 'refused') => {
    setVisitType(type);
    setShowForm(true);
  };

  // Coordenadas padrão (centro da cidade)
  const defaultCoordinates = useMemo<[number, number]>(() => {
    return [-23.5505, -46.6333]; // São Paulo
  }, []);

  // Obter localização atual
  const handleGetCurrentLocation = () => {
    setIsLoadingLocation(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation: [number, number] = [
            position.coords.latitude,
            position.coords.longitude,
          ];
          setLocation(newLocation);
          setIsLoadingLocation(false);

          // Buscar endereço automaticamente
          handleGetAddressFromCoordinates(newLocation);
        },
        (error) => {
          console.error('Erro ao obter localização:', error);
          setIsLoadingLocation(false);
          alert(
            'Não foi possível obter sua localização. Por favor, selecione manualmente no mapa.'
          );
        }
      );
    } else {
      setIsLoadingLocation(false);
      alert('Geolocalização não é suportada neste navegador.');
    }
  };

  // Atualizar localização quando o mapa é clicado ou o marcador é movido
  const handleLocationChange = (lat: number, lng: number) => {
    const newLocation: [number, number] = [lat, lng];
    setLocation(newLocation);

    // Buscar endereço automaticamente quando a localização muda
    handleGetAddressFromCoordinates(newLocation);
  };

  // Função para atualizar o formulário com os dados de endereço
  const updateFormWithAddress = (addressData: AddressData) => {
    const toStr = (v: unknown): string => (typeof v === 'string' ? v : v != null ? String(v) : '');
    setFormData((prev) => ({
      ...prev,
      street: toStr(addressData.road),
      number: toStr(addressData.house_number),
      neighborhood: toStr((addressData as any).suburb || (addressData as any).neighbourhood),
      city: toStr(addressData.city || (addressData as any).town),
      state: normalizeUF(toStr(addressData.state)),
      // Fallback: montar um display simples com rua e cidade
      locationName: (() => {
        const rua = toStr(addressData.road);
        const numero = toStr(addressData.house_number);
        if (rua) {
          return numero ? `${rua}, ${numero}` : rua;
        }
        return `${toStr(addressData.city || (addressData as any).town)}`;
      })(),
    }));
  };

  // Buscar endereço a partir das coordenadas (hoisted before effects that reference it)
  async function handleGetAddressFromCoordinates(coords: [number, number]) {
    if (!coords) return;

    setIsLoadingAddress(true);

    try {
      // Primeiro verifica no cache
      const cachedAddress = mapCacheService.findCachedAddress(coords[0], coords[1]);

      if (cachedAddress) {
        updateFormWithAddress(cachedAddress);
        setIsLoadingAddress(false);
        return;
      }

      // Se não estiver no cache, busca online
      const address = await mapCacheService.preloadAddressForLocation(coords[0], coords[1]);

      if (address) {
        updateFormWithAddress(address);
      } else {
        alert('Não foi possível obter o endereço para esta localização.');
      }
    } catch (error) {
      console.error('Erro ao buscar endereço:', error);
      alert('Erro ao buscar endereço. Verifique sua conexão.');
    } finally {
      setIsLoadingAddress(false);
    }
  }

  // Resolve city_id from UF + city name to enable neighborhood listing
  useEffect(() => {
    // Load cities list when UF changes
    const loadCities = async () => {
      const uf = (formData.state || '').trim().toUpperCase();
      if (!uf || uf.length !== 2) {
        setCityOptions([]);
        return;
      }
      setLoadingCities(true);
      try {
        const cities = await listCities({ uf, limit: 200 });
        setCityOptions(cities.map(c => ({ value: c.name, label: c.name })));
      } finally {
        setLoadingCities(false);
      }
    };
    loadCities();

    const resolveCity = async () => {
      const uf = (formData.state || '').trim().toUpperCase();
      const cityName = (formData.city || '').trim();
      if (!uf || uf.length !== 2 || !cityName) {
        setResolvedCityId(null);
        return;
      }
      try {
        const matches = await listCities({ uf, search: cityName, limit: 10 });
        const exact = matches.find(c => c.name.toLowerCase() === cityName.toLowerCase());
        setResolvedCityId(exact ? exact.id : (matches[0]?.id ?? null));
      } catch {
        setResolvedCityId(null);
      }
    };
    resolveCity();
  }, [formData.state, formData.city]);

  // Auto-geolocate when browser geolocation permission is already granted
  const autoGeoOnceRef = useRef(false);
  useEffect(() => {
    if (autoGeoOnceRef.current) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    const navAny = navigator as any;
    // Tolerant typing for browsers without the Permissions API
    const permissions = (navAny.permissions as any) as { query?: (arg: any) => Promise<any> } | undefined;
    const getAndFill = () => {
      autoGeoOnceRef.current = true;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          handleGetAddressFromCoordinates([lat, lng]);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
      );
    };
    if (permissions && typeof permissions.query === 'function') {
      permissions.query({ name: 'geolocation' as any }).then((result: any) => {
        if (result?.state === 'granted') {
          getAndFill();
        }
      }).catch(() => {});
    }
  }, []);

  // Load neighborhoods when city_id is resolved
  useEffect(() => {
    const loadNeighborhoods = async () => {
      if (!resolvedCityId) {
        setNeighborhoodOptions([]);
        return;
      }
      setLoadingNeighborhoods(true);
      try {
        const list = await listNeighborhoods({ city_id: resolvedCityId, limit: 200 });
        setNeighborhoodOptions(list.map(n => ({ value: n.name, label: n.name })));
      } finally {
        setLoadingNeighborhoods(false);
      }
    };
    loadNeighborhoods();
  }, [resolvedCityId]);

  

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      const nextValue = name === 'phone' ? formatPhoneBR(value) : value;
      setFormData((prev) => ({ ...prev, [name]: nextValue }));
    }
  };

  // Função para lidar com mudanças em componentes Select personalizados, caso necessário no futuro
  // const handleSelectChange = (name: string, value: string) => {
  //   setFormData(prev => ({ ...prev, [name]: value }));
  // };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validação básica
    if (visitType === 'person_met') {
      if (!formData.name) {
        alert('Nome é obrigatório para contatos');
        return;
      }
    }

    if (!location) {
      // Warn but proceed to allow saving other fields offline
      alert('Nenhuma localização selecionada. Salvando sem coordenadas.');
    }

    // Número obrigatório
    if (!formData.number || String(formData.number).trim() === '') {
      alert('Número é obrigatório. Caso não exista, clique no link "S/N" para preencher como sem número.');
      return;
    }

    // Tentar salvar online como Contato quando pessoa encontrada e online
    const addressParts = [
      formData.street,
      formData.number,
      formData.neighborhood,
      formData.city,
      formData.state,
    ].filter(Boolean);
    const composedAddress = addressParts.join(', ');
    const onlyDigits = (s: string) => (s || '').replace(/\D/g, '');

    const resolveTeamAndRegion = async (): Promise<{ teamId: number; regionId: number; teamName?: string } | null> => {
      const auth = useAuthStore.getState();
      
      // Primeiro, tenta obter a equipe ativa do usuário
      if (auth.user?.active_team_id) {
        try {
          // Primeiro tenta buscar a equipe usando o método getById
          try {
            const team = await teamsService.getById(auth.user.active_team_id);
            if (team) {
              const regionId = team.region_id || (team.region as { id: number } | undefined)?.id || auth.user.region_id;
              if (regionId) {
                return {
                  teamId: team.id,
                  regionId,
                  teamName: team.name
                };
              }
            }
          } catch (error) {
            console.warn('Erro ao buscar equipe por ID, tentando listar equipes do usuário...', error);
            // Se falhar, tenta buscar na lista de equipes do usuário
            const myTeams = await teamsService.my();
            const activeTeam = myTeams.find(t => t.id === auth.user?.active_team_id);
            if (activeTeam) {
              const regionId = activeTeam.region_id || (activeTeam.region as { id: number } | undefined)?.id || auth.user.region_id;
              if (regionId) {
                return {
                  teamId: activeTeam.id,
                  regionId,
                  teamName: activeTeam.name
                };
              }
            }
          }
        } catch (error) {
          console.warn('Erro ao buscar equipe ativa do usuário:', error);
        }
      }
      
      // Se não encontrar a equipe ativa, usa a lógica de fallback anterior
      const cachedRegionId = auth.activeRegionId ?? auth.user?.region_id ?? null;
      const cachedTeamId = auth.activeTeamId ?? auth.user?.active_team_id ?? null;
      
      if (cachedRegionId && cachedTeamId) {
        // Buscar nome da equipe se disponível
        let teamName = '';
        try {
          const team = await teamsService.getById(cachedTeamId);
          teamName = team.name || '';
        } catch (error) {
          console.warn('Erro ao buscar nome da equipe:', error);
        }
        return { teamId: cachedTeamId, regionId: cachedRegionId, teamName };
      }

      // Fallback to previous heuristic if cache incomplete
      let teamId: number | null = cachedTeamId;
      let regionId: number | null = cachedRegionId;
      try {
        if (!teamId && regionId) {
          const resp = await teamsService.list({ region_id: regionId, per_page: 100 });
          if (resp.data && resp.data.length > 0) {
            teamId = resp.data[0].id;
            regionId = resp.data[0].region_id;
          }
        }
        if (!teamId) {
          const anyTeams = await teamsService.list({ per_page: 100 });
          if (anyTeams.data && anyTeams.data.length > 0) {
            teamId = anyTeams.data[0].id;
            regionId = anyTeams.data[0].region_id;
          }
        }
      } catch (e) {
        console.warn('Falha ao obter times para cadastro online:', e);
      }
      return teamId && regionId ? { teamId, regionId } : null;
    };

    const tryOnline = async () => {
      const onlineNow = navigator.onLine;
      if (!onlineNow) return false;
      try {
        const ids = await resolveTeamAndRegion();
        if (!ids) {
          alert('Não foi possível determinar equipe/região para criação online. Como você está online, não salvamos em cache. Tente novamente.');
          return false;
        }
        
        // Armazenar o nome da equipe para exibição
        if (ids.teamName) {
          setSavedTeamName(ids.teamName);
        }

        const statusMap: Record<string, 'potential' | 'absent' | 'refused'> = {
          person_met: 'potential',
          not_home: 'absent',
          refused: 'refused',
        };
        const status = statusMap[visitType] ?? 'potential';

        // Nome obrigatório: para não pessoa encontrada, derive do local/endereço
        const fallbackName = formData.locationName || composedAddress || 'Visita de Campo';
        const name = visitType === 'person_met' ? formData.name : fallbackName;

        const payload = {
          name,
          email: formData.email || null,
          phone: visitType === 'person_met' && formData.phone ? onlyDigits(formData.phone) : null,
          gender: formData.gender || null,
          age_group: formData.age_group || null,
          address: composedAddress || formData.locationName || null,
          // Structured address fields to persist granular data
          street: formData.street || null,
          number: formData.number || null,
          complement: formData.address_complement || null,
          address_complement: formData.address_complement || null,
          neighborhood: formData.neighborhood || null,
          city: formData.city || null,
          state: formData.state || null,
          // Coordinates when available
          lat: location ? location[0] : null,
          lng: location ? location[1] : null,
          address_lat: location ? location[0] : null,
          address_lng: location ? location[1] : null,
          status,
          team_id: ids.teamId,
          region_id: ids.regionId,
          notes: formData.observations || null,
        } as const;

        const created = await contactsService.create(payload);
        setSavedContactId(String(created.id));
        setSavedOnline(true);
        return true;
      } catch (error) {
        let message = 'Erro desconhecido';
        if (error instanceof Error) message = error.message;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const maybe = error as any;
        if (maybe?.response?.data) {
          const data = maybe.response.data;
          if (typeof data === 'string') {
            message = data;
          } else {
            try {
              message = JSON.stringify(data);
            } catch {
              /* no-op */
            }
          }
        }
        console.warn('Falha ao criar contato online:', message);
        alert(`Falha ao criar contato online: ${message}. Como você está online, não salvamos em cache.`);
        return false;
      }
    };

    const saveOffline = async () => {
      // Defensive: never save to cache when browser is online (any visit type)
      const onlineNow = navigator.onLine;
      if (onlineNow) {
        console.debug('[FieldCadastroModal] Skipping saveOffline because onlineNow=true');
        return;
      }
      // Resolve IDs to help future sync
      const ids = await resolveTeamAndRegion();
      const teamId = ids?.teamId ?? null;
      const regionId = ids?.regionId ?? null;
      
      // Armazenar o nome da equipe para exibição
      if (ids?.teamName) {
        setSavedTeamName(ids.teamName);
      }
      const visitData = {
        user_id: user?.id,
        type: visitType,
        ...formData,
        team_id: teamId,
        region_id: regionId,
        lat: location ? location[0] : undefined,
        lng: location ? location[1] : undefined,
      };
      console.debug('[FieldCadastroModal] Saving offline visit:', { type: visitType, visitData });
      const saved = offlineStorageService.saveOfflineVisit(visitData);
      setSavedContactId(String(saved.id));
      const visits = offlineStorageService.getOfflineVisits();
      setOfflineCount(visits.length);
    };

    // Fluxo de salvamento
    (async () => {
      const onlineOk = await tryOnline();
      const onlineNow = navigator.onLine;

      // Para visitas que não criam contato online:
      // - Online: não salvar em cache; apenas mostrar sucesso
      // - Offline: salvar em cache e mostrar sucesso
      if (visitType !== 'person_met') {
        if (onlineNow) {
          // Só mostra sucesso se criação online ocorreu
          if (onlineOk) {
            console.debug('[FieldCadastroModal] Non-contact online created as contact successfully');
            setSavedOnline(true);
            setShowSuccessDialog(true);
          }
          return;
        }
        await saveOffline();
        setSavedOnline(false);
        setShowSuccessDialog(true);
        return;
      }

      if (onlineNow) {
        // Online: apenas sucesso online mostra sucesso; sem fallback para cache
        if (onlineOk) {
          setShowSuccessDialog(true);
        }
        // Caso onlineOk=false, já alertamos acima e não salvamos offline
        return;
      } else {
        // Offline: salvar no cache
        await saveOffline();
        setSavedOnline(false);
        setShowSuccessDialog(true);
      }
    })();
  };

  const handleStudySave = async (studyData: StudyFormData): Promise<boolean> => {
    try {
      const contactIdNum = Number(studyData.contactId);
      if (!contactIdNum || Number.isNaN(contactIdNum)) {
        toast.error('Contato inválido.');
        return false;
      }

      // Determine team to associate with the schedule
      const ids = await resolveTeamAndRegionForSchedules();
      const teamId = ids?.teamId;
      if (!teamId) {
        toast.error('Equipe não encontrada.');
        return false;
      }

      // Map fields to StudySchedulesService.create payload
      const study_material_id = Number(studyData.studyType); // Study type select uses IDs as values
      if (!study_material_id || Number.isNaN(study_material_id)) {
        toast.error('Tipo de estudo obrigatório.');
        return false;
      }

      const currentStatus: StudyScheduleStatus | undefined = studyData.completed ? 'completed' : undefined; // backend default = 'incomplete'

      // Create current study schedule
      await studySchedulesService.create({
        contact_id: contactIdNum,
        team_id: teamId,
        study_material_id,
        study_date: studyData.studyDate,
        study_time: (studyData.studyTime && studyData.studyTime.trim() !== '') ? studyData.studyTime : '09:00',
        notes: studyData.observations || null,
        ...(currentStatus ? { status: currentStatus } : {}),
      });

      // Optionally schedule next study as a new schedule with default status
      if (studyData.scheduleNext && studyData.nextStudyDate) {
        await studySchedulesService.create({
          contact_id: contactIdNum,
          team_id: teamId,
          study_material_id,
          study_date: studyData.nextStudyDate,
          study_time: (studyData.nextStudyTime && studyData.nextStudyTime.trim() !== '') ? studyData.nextStudyTime : '09:00',
          notes: 'Próximo estudo agendado',
        });
      }

      toast.success('Estudo salvo.' + (studyData.scheduleNext && studyData.nextStudyDate ? ' Próximo agendado.' : ''));
      return true;
    } catch (err) {
      let message = 'Erro ao salvar estudo.';
      if (err instanceof Error && err.message) message = err.message;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const maybe: any = err;
      if (maybe?.response?.data) {
        try {
          message = typeof maybe.response.data === 'string' ? maybe.response.data : JSON.stringify(maybe.response.data);
        } catch {
          /* noop */
        }
      }
      console.error('[handleStudySave] Falha ao registrar/agendar estudo (study_schedules):', { error: err, message });
      toast.error(message, { toastId: 'study-save-error' });
      return false;
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccessDialog(false);
    // Set a flag so ContactsPage can auto-refresh with quick filter logic
    try { sessionStorage.setItem('contacts:postSaveRefresh', '1'); } catch {}
    // Close this modal
    onClose();
    // Redirect to /contacts or refresh if already there
    if (routerLocation.pathname !== '/contacts') {
      navigate('/contacts', { replace: true });
    } else {
      // Full reload to ensure fresh data, as requested
      try {
        window.location.reload();
      } catch {
        // Fallback: navigate to same route
        navigate(0);
      }
    }
  };

  if (!isOpen) return null;

  // Renderizar modais adicionais
  if (showSuccessDialog) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-green-100 p-2 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <h3 className="text-lg font-medium text-center mb-2">
            Visita registrada com sucesso!
          </h3>
          <p className="text-gray-500 text-center mb-4">
            {visitType === 'person_met'
              ? (savedOnline ? 'Contato criado no sistema.' : 'Contato salvo offline (será sincronizado).')
              : 'A tentativa de visita foi registrada.'}
          </p>
          {savedTeamName && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
              <p className="text-sm text-blue-800 text-center">
                Este contato será associado à equipe: <span className="font-semibold">{savedTeamName}</span>
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            {visitType === 'person_met' && formData.name && (
              <Button
                type="button"
                className="flex-1"
                onClick={() => {
                  // Close success dialog first so the study modal can render
                  setShowSuccessDialog(false);
                  setShowStudyModal(true);
                }}
                leftIcon={<BookOpen className="w-4 h-4" />}
              >
                Registrar Estudo
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={handleCloseSuccess}
            >
              Concluir
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (showStudyModal) {
    return (
      <StudyRegistrationModal
        isOpen={true}
        onClose={() => {
          setShowStudyModal(false);
          onClose();
        }}
        contactId={savedContactId}
        contactName={formData.name}
        onSave={handleStudySave}
      />
    );
  }

  // Renderizar o modal principal
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Registrar Visita de Campo"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {activeTeamName ? (
          <div className="text-sm text-gray-600">
            Equipe ativa:{' '}
            <button
              type="button"
              onClick={() => { onClose(); navigate('/profile'); }}
              className="font-medium text-primary-600 underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
              title="Abrir perfil para gerenciar equipes"
            >
              {activeTeamName}
            </button>
          </div>
        ) : null}
        {/* Tipo de visita */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipo de Visita
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={visitType === 'person_met' ? 'primary' : 'secondary'}
              onClick={() => chooseVisitType('person_met')}
              className="flex-1"
            >
              Pessoa Encontrada
            </Button>
            <Button
              type="button"
              variant={visitType === 'not_home' ? 'primary' : 'secondary'}
              onClick={() => chooseVisitType('not_home')}
              className="flex-1"
            >
              Não Estava em Casa
            </Button>
            <Button
              type="button"
              variant={visitType === 'refused' ? 'primary' : 'secondary'}
              onClick={() => chooseVisitType('refused')}
              className="flex-1"
            >
              Recusou Contato
            </Button>
          </div>
        </div>
        {showForm && (
        <>
        {/* Localização */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Localização
            </label>
            <div className="flex items-center space-x-2">
              <span
                className={`text-xs ${isOnline ? 'text-green-600' : 'text-red-600'}`}
              >
                {isOnline ? (
                  <>
                    <Wifi className="inline w-3 h-3 mr-1" /> Online
                  </>
                ) : (
                  <>
                    <WifiOff className="inline w-3 h-3 mr-1" /> Offline
                  </>
                )}
              </span>
              <Button
                type="button"
                size="sm"
                onClick={handleGetCurrentLocation}
                disabled={isLoadingLocation}
                leftIcon={
                  isLoadingLocation ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <MapPin className="w-3 h-3" />
                  )
                }
              >
                {isLoadingLocation ? 'Obtendo...' : 'Minha Localização'}
              </Button>
            </div>
          </div>

          {/* Mapa interativo */}
          <div className="h-64 bg-gray-100 rounded-lg overflow-hidden mb-2">
            <MapDisplay
              initialLocation={location ? { lat: location[0], lng: location[1] } : { lat: defaultCoordinates[0], lng: defaultCoordinates[1] }}
              onLocationChange={handleLocationChange}
              height="100%"
              interactive={true}
            />
          </div>

          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">
              {location
                ? `Lat: ${location[0].toFixed(6)}, Lng: ${location[1].toFixed(6)}`
                : 'Nenhuma localização selecionada'}
            </span>
            <div className="flex items-center space-x-2">
              {location && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    const url = `https://www.google.com/maps?q=${location[0]},${location[1]}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  leftIcon={<Map className="w-3 h-3" />}
                >
                  Abrir no Google Maps
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                onClick={() =>
                  handleGetAddressFromCoordinates(location || defaultCoordinates)
                }
                disabled={!location || isLoadingAddress}
                leftIcon={
                  isLoadingAddress ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <Map className="w-3 h-3" />
                  )
                }
              >
                {isLoadingAddress ? 'Buscando...' : 'Obter Endereço'}
              </Button>
            </div>
          </div>
        </div>

        {/* Dados do contato (apenas se for pessoa encontrada) */}
        {visitType === 'person_met' && (
          <div className="space-y-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome
              </label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Nome completo"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(00) 00000-0000"
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-2 mb-4">
              <input
                id="consent_whatsapp"
                type="checkbox"
                className="h-4 w-4"
                checked={formData.consent_whatsapp}
                onChange={(e) => setFormData(prev => ({ ...prev, consent_whatsapp: e.target.checked }))}
                disabled={!isValidPhone(formData.phone)}
              />
              <label htmlFor="consent_whatsapp" className="text-sm text-gray-700">
                Aceita receber mensagens da Bíblia/meditação
                {!isValidPhone(formData.phone) && (
                  <span className="ml-2 text-gray-400">(informe um telefone válido)</span>
                )}
              </label>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between">
                {showEmail && (
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                )}
                <button
                  type="button"
                  className="text-blue-600 text-xs underline hover:text-blue-700"
                  onClick={() => setShowEmail(prev => !prev)}
                >
                  {showEmail ? 'Ocultar' : 'Adicionar e-mail'}
                </button>
              </div>
              {showEmail && (
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="email@exemplo.com"
                />
              )}
            </div>
          </div>
        )}

        {/* Campos de UF/Cidade/Bairro antes do Nome do Local */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <Autocomplete
              value={(formData.state || '').toUpperCase()}
              onChange={(val) => {
                if (!val) return;
                const uf = normalizeUF(val);
                if (uf) {
                  setFormData(prev => ({ ...prev, state: uf, city: '', neighborhood: '' }));
                }
              }}
              options={UF_OPTIONS}
              placeholder="Selecione o estado (UF)"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cidade
            </label>
            <Autocomplete
              value={formData.city || ''}
              onChange={(val) => setFormData(prev => ({ ...prev, city: val || '', neighborhood: '' }))}
              options={cityOptions}
              placeholder={loadingCities ? 'Carregando cidades...' : (formData.state ? 'Digite ou selecione a cidade' : 'Selecione UF')}
              disabled={!formData.state}
              helperInfo={formData.state ? 'Você pode digitar uma nova cidade e pressionar Enter para confirmar.' : undefined}
              allowCustom={true}
            />
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bairro
              </label>
              <button
                type="button"
                className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100"
                onClick={() => setShowNeighborhoodTip(prev => !prev)}
                aria-label="Dica sobre o nome do bairro"
                title="Clique para ver a dica"
              >
                <Info className="w-4 h-4" />
              </button>
            </div>
            <Autocomplete
              value={formData.neighborhood || ''}
              onChange={(val) => setFormData(prev => ({ ...prev, neighborhood: val || '' }))}
              options={neighborhoodOptions}
              placeholder={
                loadingNeighborhoods
                  ? 'Carregando bairros...'
                  : (
                      resolvedCityId
                        ? 'Digite ou selecione o bairro'
                        : ((formData.city || '').trim() !== ''
                            ? 'Digite o bairro (cidade informada manualmente)'
                            : 'Selecione/defina a cidade para liberar o bairro')
                    )
              }
              disabled={!(Boolean(resolvedCityId) || ((formData.city || '').trim() !== ''))}
              helperInfo={resolvedCityId ? 'Você pode digitar um novo bairro e pressionar Enter para confirmar.' : undefined}
              allowCustom={true}
              helperText={showNeighborhoodTip ? 'Antes de definir um nome de bairro, abra o Google Mapas (no botão abaixo do mapa) para conferir o nome correto. Se nem no Google constar (casos comuns em cidades pequenas), use "Centro".' : undefined}
            />
          </div>

        </div>

        {/* Endereço */}
        {visitType !== 'person_met' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Local = Nome do Contato
            </label>
            <Input
              name="locationName"
              value={formData.locationName}
              onChange={handleInputChange}
              placeholder="Ex: Casa, Trabalho, etc. (padrão: Nome do contato)"
              helperText="Para este caso, o endereço será o Nome do contato."
            />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rua
            </label>
            <Input
              name="street"
              value={formData.street}
              onChange={handleInputChange}
              placeholder="Nome da rua"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="mr-2">Número</span>
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline focus:outline-none"
                onClick={() => setFormData(prev => ({ ...prev, number: 's/n' }))}
                title="Sem número"
              >
                S/N
              </button>
            </label>
            <Input
              name="number"
              value={formData.number}
              onChange={handleInputChange}
              placeholder="Número"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Complemento
            </label>
            <Input
              name="address_complement"
              value={formData.address_complement}
              onChange={handleInputChange}
              placeholder="Complemento (apto, bloco, referência)"
            />
          </div>
        </div>

        {/* Gênero e Faixa etária (apenas quando 'Pessoa Encontrada') */}
        {visitType === 'person_met' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Gênero</label>
              <Select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                options={[
                  { value: 'masculino', label: 'Masculino' },
                  { value: 'feminino', label: 'Feminino' },
                ]}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Faixa Etária</label>
              <Select
                name="age_group"
                value={formData.age_group}
                onChange={handleInputChange}
                options={[
                  { value: 'crianca', label: 'Criança' },
                  { value: 'jovem', label: 'Jovem' },
                  { value: 'adulto', label: 'Adulto' },
                  { value: 'senior', label: 'Sênior' },
                ]}
              />
            </div>
          </div>
        )}

        {/* Observações (sempre visível) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Observações
          </label>
          <textarea
            name="observations"
            value={formData.observations}
            onChange={handleInputChange}
            rows={3}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Informações adicionais sobre a visita..."
          />
        </div>

        {/* Botões */}
        <div className="flex justify-between items-center space-x-2">
          <span className="text-xs text-gray-500">Salvos offline: {offlineCount}</span>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            leftIcon={<X className="w-4 h-4" />}
          >
            Cancelar
          </Button>
          {showForm && (
            <Button type="submit" leftIcon={<CheckCircle className="w-4 h-4" />}>
              Salvar
            </Button>
          )}
        </div>
        </>
        )}
      </form>
    </Modal>
  );
};

export default FieldCadastroModal;
